import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EventData, Step } from 'react-joyride';
import { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { api } from '@/lib/api';
import type { UserRole } from '@/stores/auth';
import { isBctStaff, useAuthStore } from '@/stores/auth';

// ============================================================================
// Types
// ============================================================================

export type TourId =
  | 'dashboard'
  | 'issues'
  | 'projects'
  | 'project-detail'
  | 'test-execution'
  | 'documents'
  | 'issue-detail'
  | 'test-run'
  | 'checklist'
  | 'runbook';

export interface TourState {
  completed?: boolean;
  skipped?: boolean;
  version?: number;
  completedAt?: string;
}

export interface OnboardingState {
  tours?: Record<string, TourState>;
  welcomeCompleted?: boolean;
  checklist?: Record<string, boolean>;
}

type RoleCategory = 'staff' | 'customer' | 'customerAdmin';

// ============================================================================
// Tour Priority - Controls auto-start order for new users
// ============================================================================

const TOUR_PRIORITY: Record<TourId, number> = {
  dashboard: 1,
  projects: 2,
  issues: 3,
  'issue-detail': 4,
  documents: 5,
  'test-execution': 6,
  'project-detail': 7,
  'test-run': 8,
  checklist: 9,
  runbook: 10,
};

// Tours that must be completed/skipped before lower-priority tours auto-start
const PRIORITY_GATE_TOURS: TourId[] = ['dashboard', 'projects', 'issues'];

// ============================================================================
// Session-Based First Visit Tracking
// ============================================================================

const SESSION_VISITED_KEY = 'onboarding:visited';

/**
 * Checks if a tour has been visited in the current browser session.
 * This prevents tours from re-triggering on page reloads while still
 * allowing them to show on first visit after login.
 */
function hasVisitedInSession(tourId: string): boolean {
  try {
    const visited = sessionStorage.getItem(SESSION_VISITED_KEY);
    const visitedTours = visited ? JSON.parse(visited) : [];
    return visitedTours.includes(tourId);
  } catch {
    return false;
  }
}

/**
 * Marks a tour as visited in the current browser session.
 */
function markVisitedInSession(tourId: string): void {
  try {
    const visited = sessionStorage.getItem(SESSION_VISITED_KEY);
    const visitedTours = visited ? JSON.parse(visited) : [];
    if (!visitedTours.includes(tourId)) {
      visitedTours.push(tourId);
      sessionStorage.setItem(SESSION_VISITED_KEY, JSON.stringify(visitedTours));
    }
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clears the session visit tracking. Called on fresh login to allow
 * tours to show again on first visit to each page.
 */
export function clearOnboardingVisitedSession(): void {
  try {
    sessionStorage.removeItem(SESSION_VISITED_KEY);
  } catch {
    // Ignore storage errors
  }
}

// ============================================================================
// Tour Versions - Increment to re-trigger tours for all users
// ============================================================================

const TOUR_VERSIONS: Record<TourId, number> = {
  dashboard: 2, // v2: Fixed race condition that prevented auto-start
  issues: 2, // v2: Enhanced with more comprehensive steps
  projects: 1,
  'project-detail': 1,
  'test-execution': 1,
  documents: 1,
  'issue-detail': 3, // v3: Fixed missing data-tour attrs and stale selector
  'test-run': 1,
  checklist: 1,
  runbook: 1,
};

// ============================================================================
// Tour Steps by Role and Tour Type
// ============================================================================

function getTourSteps(
  t: (key: string) => string,
  roleCategory: RoleCategory,
  tourId: TourId,
): Step[] {
  const staffSteps: Record<TourId, Step[]> = {
    dashboard: [
      {
        target: '[data-tour="tenant-switcher"]',
        content: t('help:tours.staff.dashboard.tenantSwitcher'),
        skipBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="my-work"]',
        content: t('help:tours.staff.dashboard.myWork'),
        placement: 'top',
      },
    ],
    issues: [
      {
        target: '[data-tour="issue-filters"]',
        content: t('help:tours.staff.issues.filters'),
        skipBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="issue-search"]',
        content: t('help:tours.staff.issues.search'),
        placement: 'bottom',
      },
      {
        target: '[data-tour="issue-view-mode"]',
        content: t('help:tours.staff.issues.viewMode'),
        placement: 'left',
      },
      {
        target: '[data-tour="issue-create"]',
        content: t('help:tours.staff.issues.create'),
        placement: 'left',
      },
    ],
    projects: [
      {
        target: '[data-tour="project-filters"]',
        content: t('help:tours.staff.projects.filters'),
        skipBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="project-search"]',
        content: t('help:tours.staff.projects.search'),
        placement: 'bottom',
      },
      {
        target: '[data-tour="project-view-mode"]',
        content: t('help:tours.staff.projects.viewMode'),
        placement: 'left',
      },
      {
        target: '[data-tour="project-create"]',
        content: t('help:tours.staff.projects.create'),
        placement: 'left',
      },
    ],
    'project-detail': [
      {
        target: '[data-tour="project-header"]',
        content: t('help:tours.staff.projectDetail.header'),
        skipBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="project-phase"]',
        content: t('help:tours.staff.projectDetail.phase'),
        placement: 'bottom',
      },
      {
        target: '[data-tour="project-tabs"]',
        content: t('help:tours.staff.projectDetail.tabs'),
        placement: 'bottom',
      },
      {
        target: '[data-tour="project-updates"]',
        content: t('help:tours.staff.projectDetail.updates'),
        placement: 'right',
      },
      {
        target: '[data-tour="project-settings"]',
        content: t('help:tours.staff.projectDetail.settings'),
        placement: 'left',
      },
    ],
    'test-execution': [
      {
        target: '[data-tour="test-plan-select"]',
        content: t('help:tours.staff.testExecution.planSelect'),
        skipBeacon: true,
      },
      {
        target: '[data-tour="test-progress"]',
        content: t('help:tours.staff.testExecution.progress'),
        placement: 'left',
      },
      {
        target: '[data-tour="test-case-result"]',
        content: t('help:tours.staff.testExecution.caseResult'),
        placement: 'right',
      },
    ],
    documents: [
      {
        target: '[data-tour="document-upload"]',
        content: t('help:tours.staff.documents.upload'),
        skipBeacon: true,
      },
      {
        target: '[data-tour="document-folders"]',
        content: t('help:tours.staff.documents.folders'),
        placement: 'right',
      },
    ],
    'issue-detail': [
      {
        target: '[data-tour="issue-header"]',
        content: t('help:tours.staff.issueDetail.header'),
        skipBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="issue-status"]',
        content: t('help:tours.staff.issueDetail.statusStepper'),
        placement: 'bottom',
      },
      {
        target: '[data-tour="issue-assign"]',
        content: t('help:tours.staff.issueDetail.assign'),
        placement: 'left',
      },
      {
        target: '[data-tour="issue-time-entries"]',
        content: t('help:tours.staff.issueDetail.timeEntries'),
        placement: 'bottom',
      },
    ],
    'test-run': [
      {
        target: '[data-tour="test-case"]',
        content: t('help:tours.staff.testRun.case'),
        skipBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="test-progress"]',
        content: t('help:tours.staff.testRun.progress'),
        placement: 'left',
      },
      {
        target: '[data-tour="test-case-result"]',
        content: t('help:tours.staff.testRun.result'),
        placement: 'top',
      },
      {
        target: '[data-tour="test-notes"]',
        content: t('help:tours.staff.testRun.notes'),
        placement: 'top',
      },
    ],
    checklist: [
      {
        target: '[data-tour="checklist-categories"]',
        content: t('help:tours.staff.checklist.categories'),
        skipBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="checklist-evidence"]',
        content: t('help:tours.staff.checklist.evidence'),
        placement: 'right',
      },
      {
        target: '[data-tour="checklist-apply-template"]',
        content: t('help:tours.staff.checklist.applyTemplate'),
        placement: 'left',
      },
      {
        target: '[data-tour="checklist-lock"]',
        content: t('help:tours.staff.checklist.lock'),
        placement: 'bottom',
      },
    ],
    runbook: [
      {
        target: '[data-tour="runbook-phases"]',
        content: t('help:tours.staff.runbook.phases'),
        skipBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="runbook-current-action"]',
        content: t('help:tours.staff.runbook.currentAction'),
        placement: 'right',
      },
      {
        target: '[data-tour="runbook-action-owner"]',
        content: t('help:tours.staff.runbook.actionOwner'),
        placement: 'left',
      },
      {
        target: '[data-tour="runbook-go-nogo"]',
        content: t('help:tours.staff.runbook.goNoGo'),
        placement: 'top',
      },
      {
        target: '[data-tour="runbook-log"]',
        content: t('help:tours.staff.runbook.log'),
        placement: 'top',
      },
      {
        target: '[data-tour="runbook-rollback"]',
        content: t('help:tours.staff.runbook.rollback'),
        placement: 'top',
      },
    ],
  };

  const customerSteps: Record<TourId, Step[]> = {
    dashboard: [
      {
        target: '[data-tour="task-filters"]',
        content: t('help:tours.customer.dashboard.taskFilters'),
        skipBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="my-tasks"]',
        content: t('help:tours.customer.dashboard.myTasks'),
        placement: 'top',
      },
      {
        target: '[data-tour="project-progress"]',
        content: t('help:tours.customer.dashboard.projectProgress'),
        placement: 'left',
      },
    ],
    issues: [
      {
        target: '[data-tour="issue-filters"]',
        content: t('help:tours.customer.issues.filters'),
        skipBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="issue-search"]',
        content: t('help:tours.customer.issues.search'),
        placement: 'bottom',
      },
      {
        target: '[data-tour="issue-view-mode"]',
        content: t('help:tours.customer.issues.viewMode'),
        placement: 'left',
      },
      {
        target: '[data-tour="issue-create"]',
        content: t('help:tours.customer.issues.create'),
        placement: 'left',
      },
    ],
    projects: [
      {
        target: '[data-tour="project-filters"]',
        content: t('help:tours.customer.projects.filters'),
        skipBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="project-search"]',
        content: t('help:tours.customer.projects.search'),
        placement: 'bottom',
      },
      {
        target: '[data-tour="project-view-mode"]',
        content: t('help:tours.customer.projects.viewMode'),
        placement: 'left',
      },
    ],
    'project-detail': [
      {
        target: '[data-tour="project-header"]',
        content: t('help:tours.customer.projectDetail.header'),
        skipBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="project-phase"]',
        content: t('help:tours.customer.projectDetail.phase'),
        placement: 'bottom',
      },
      {
        target: '[data-tour="project-tabs"]',
        content: t('help:tours.customer.projectDetail.tabs'),
        placement: 'bottom',
      },
      {
        target: '[data-tour="project-updates"]',
        content: t('help:tours.customer.projectDetail.updates'),
        placement: 'right',
      },
    ],
    'test-execution': [
      {
        target: '[data-tour="test-plan-select"]',
        content: t('help:tours.customer.testExecution.planSelect'),
        skipBeacon: true,
      },
      {
        target: '[data-tour="test-progress"]',
        content: t('help:tours.customer.testExecution.progress'),
        placement: 'left',
      },
      {
        target: '[data-tour="test-case-result"]',
        content: t('help:tours.customer.testExecution.caseResult'),
        placement: 'right',
      },
    ],
    documents: [
      {
        target: '[data-tour="project-documents"]',
        content: t('help:tours.customer.documents.projectDocuments'),
        skipBeacon: true,
      },
      {
        target: '[data-tour="document-upload"]',
        content: t('help:tours.customer.documents.upload'),
        placement: 'left',
      },
    ],
    'issue-detail': [
      {
        target: '[data-tour="issue-header"]',
        content: t('help:tours.customer.issueDetail.header'),
        skipBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="issue-status"]',
        content: t('help:tours.customer.issueDetail.status'),
        placement: 'bottom',
      },
      {
        target: '[data-tour="issue-activity"]',
        content: t('help:tours.customer.issueDetail.comment'),
        placement: 'top',
      },
      {
        target: '[data-tour="issue-files"]',
        content: t('help:tours.customer.issueDetail.files'),
        placement: 'top',
      },
      {
        target: '[data-tour="issue-watch"]',
        content: t('help:tours.customer.issueDetail.watch'),
        placement: 'left',
      },
    ],
    'test-run': [
      {
        target: '[data-tour="test-case"]',
        content: t('help:tours.customer.testRun.case'),
        skipBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="test-progress"]',
        content: t('help:tours.customer.testRun.progress'),
        placement: 'left',
      },
      {
        target: '[data-tour="test-case-result"]',
        content: t('help:tours.customer.testRun.result'),
        placement: 'top',
      },
      {
        target: '[data-tour="test-notes"]',
        content: t('help:tours.customer.testRun.notes'),
        placement: 'top',
      },
    ],
    checklist: [
      {
        target: '[data-tour="checklist-categories"]',
        content: t('help:tours.customer.checklist.categories'),
        skipBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="checklist-item"]',
        content: t('help:tours.customer.checklist.assignedItems'),
        placement: 'right',
      },
      {
        target: '[data-tour="checklist-evidence"]',
        content: t('help:tours.customer.checklist.evidence'),
        placement: 'right',
      },
    ],
    runbook: [
      {
        target: '[data-tour="runbook-phases"]',
        content: t('help:tours.customer.runbook.phases'),
        skipBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-tour="runbook-current-action"]',
        content: t('help:tours.customer.runbook.currentAction'),
        placement: 'right',
      },
      {
        target: '[data-tour="runbook-action-owner"]',
        content: t('help:tours.customer.runbook.actionOwner'),
        placement: 'left',
      },
      {
        target: '[data-tour="runbook-go-nogo"]',
        content: t('help:tours.customer.runbook.goNoGo'),
        placement: 'top',
      },
      {
        target: '[data-tour="runbook-log"]',
        content: t('help:tours.customer.runbook.log'),
        placement: 'top',
      },
      {
        target: '[data-tour="runbook-rollback"]',
        content: t('help:tours.customer.runbook.rollback'),
        placement: 'top',
      },
    ],
  };

  // Customer Admin gets the same steps as regular customers
  // Additional admin-specific steps can be added here when admin UI elements exist
  const customerAdminSteps: Record<TourId, Step[]> = {
    ...customerSteps,
  };

  if (roleCategory === 'staff') {
    return staffSteps[tourId] || [];
  } else if (roleCategory === 'customerAdmin') {
    return customerAdminSteps[tourId] || [];
  } else {
    return customerSteps[tourId] || [];
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

function getRoleCategory(role: UserRole | undefined): RoleCategory {
  if (!role) return 'customer';
  if (isBctStaff(role)) return 'staff';
  if (role === 'CUSTOMER_ADMIN') return 'customerAdmin';
  return 'customer';
}

const STORAGE_KEY = 'pregoo-onboarding';

function getLocalState(): OnboardingState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : { tours: {} };
  } catch {
    return { tours: {} };
  }
}

function clearLocalState() {
  localStorage.removeItem(STORAGE_KEY);
}

// ============================================================================
// API Functions
// ============================================================================

async function fetchOnboardingState(): Promise<OnboardingState> {
  try {
    const response = await api.get<OnboardingState>('/users/me/onboarding');
    return response.data || { tours: {} };
  } catch {
    // Fall back to local storage if API fails
    return getLocalState();
  }
}

async function updateOnboardingState(
  tourId: string,
  action: 'complete' | 'skip' | 'reset',
  version?: number,
): Promise<OnboardingState | null> {
  try {
    const response = await api.patch<OnboardingState>('/users/me/onboarding', {
      tourId,
      action,
      version,
    });
    return response.data;
  } catch {
    // Fall back to local storage if API fails
    return null;
  }
}

async function migrateLocalStorage(): Promise<boolean> {
  try {
    const localState = getLocalState();
    if (!localState.tours || Object.keys(localState.tours).length === 0) {
      return false;
    }

    // Convert old format to new format for migration
    const completedTours = Object.entries(localState.tours)
      .filter(([, state]) => state.completed)
      .map(([id]) => id);
    const skippedTours = Object.entries(localState.tours)
      .filter(([, state]) => state.skipped)
      .map(([id]) => id);

    await api.patch('/users/me/onboarding', {
      action: 'migrate',
      state: { completedTours, skippedTours },
    });

    clearLocalState();
    return true;
  } catch {
    return false;
  }
}

async function logOnboardingEvent(
  eventType: string,
  eventData?: Record<string, unknown>,
): Promise<void> {
  try {
    await api.post('/analytics/onboarding', { eventType, eventData });
  } catch {
    // Silently fail - analytics should never break the app
  }
}

// ============================================================================
// Filter steps that have valid targets
// ============================================================================

function filterValidSteps(steps: Step[]): Step[] {
  return steps.filter((step) => {
    if (typeof step.target !== 'string') return true;
    const element = document.querySelector(step.target);
    return element !== null;
  });
}

// ============================================================================
// Main Hook
// ============================================================================

export function useOnboarding(tourId: TourId) {
  const { t } = useTranslation('help');
  const { user } = useAuthStore();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [state, setState] = useState<OnboardingState>({ tours: {} });
  // Initialize isLoading based on whether we have a user (avoids synchronous setState in effect)
  const [isLoading, setIsLoading] = useState(() => !!user);
  const hasMigratedRef = useRef(false);

  const roleCategory = getRoleCategory(user?.role);
  const currentVersion = TOUR_VERSIONS[tourId];

  // Get steps based on role
  const allSteps = useMemo(() => getTourSteps(t, roleCategory, tourId), [t, roleCategory, tourId]);

  // Filter to only steps with valid targets
  const [steps, setSteps] = useState<Step[]>([]);

  // Load onboarding state from backend (with localStorage migration)
  useEffect(() => {
    if (!user) {
      return;
    }

    // The load outlives the effect that started it. Whoever resolves last writes last, so
    // without this a slower answer for the previous user overwrites the current one. The
    // flag is read after every await and not only before the first: the writes that matter
    // sit behind the last one.
    let cancelled = false;

    async function loadState() {
      setIsLoading(true);

      // Try to migrate localStorage data first (one-time)
      if (!hasMigratedRef.current) {
        await migrateLocalStorage();
        // Recorded before bailing out: the migration did run, and that fact belongs to the
        // app rather than to this effect instance.
        hasMigratedRef.current = true;
        if (cancelled) return;
      }

      const backendState = await fetchOnboardingState();
      if (cancelled) return;
      setState(backendState);
      setIsLoading(false);
    }

    loadState();

    return () => {
      cancelled = true;
    };
  }, [user]);

  // Check if tour should auto-start
  useEffect(() => {
    if (isLoading || !user || allSteps.length === 0) return;

    const tourState = state.tours?.[tourId];
    const hasCompleted = tourState?.completed;
    const hasSkipped = tourState?.skipped;
    const savedVersion = tourState?.version || 0;

    // Already completed or skipped at current version? Never auto-start
    if ((hasCompleted || hasSkipped) && savedVersion >= currentVersion) {
      return;
    }

    // Version bump = tour was updated, always show (even on reload)
    const isVersionBump = savedVersion > 0 && savedVersion < currentVersion;

    // For customer users: suppress lower-priority tours until gate tours are done
    if (!isBctStaff(user.role) && !isVersionBump) {
      const myPriority = TOUR_PRIORITY[tourId] || 99;
      const gateCompleted = PRIORITY_GATE_TOURS.every((gateTourId) => {
        const gateState = state.tours?.[gateTourId];
        return gateState?.completed || gateState?.skipped;
      });
      // If gate tours aren't done and this tour is below the gate, suppress
      if (
        !gateCompleted &&
        myPriority > TOUR_PRIORITY[PRIORITY_GATE_TOURS[PRIORITY_GATE_TOURS.length - 1]]
      ) {
        return;
      }
    }

    // First visit to this page in current session?
    const isFirstVisitInSession = !hasVisitedInSession(tourId);

    // Only auto-start if:
    // 1. First visit to page in session (not a reload), OR
    // 2. Tour version was bumped (force re-education)
    if (!(isFirstVisitInSession || isVersionBump)) {
      return;
    }

    // Mark as visited BEFORE starting tour (prevent race conditions)
    markVisitedInSession(tourId);

    // Delay to let page render, then filter valid steps
    const timer = setTimeout(() => {
      const validSteps = filterValidSteps(allSteps);
      if (validSteps.length > 0) {
        setSteps(validSteps);
        setRun(true);
        logOnboardingEvent('tour_started', {
          tourId,
          isVersionBump,
          isFirstVisit: isFirstVisitInSession,
        });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [tourId, state, isLoading, user, allSteps, currentVersion]);

  // Handle Joyride callbacks
  const handleCallback = useCallback(
    async (data: EventData) => {
      const { status, index, action, type } = data;

      // Handle tour completion
      if (status === STATUS.FINISHED) {
        await updateOnboardingState(tourId, 'complete', currentVersion);
        setState((prev) => ({
          ...prev,
          tours: {
            ...prev.tours,
            [tourId]: {
              completed: true,
              version: currentVersion,
              completedAt: new Date().toISOString(),
            },
          },
        }));
        setRun(false);
        logOnboardingEvent('tour_completed', { tourId });
        return;
      }

      // Handle tour skip
      if (status === STATUS.SKIPPED) {
        await updateOnboardingState(tourId, 'skip', currentVersion);
        setState((prev) => ({
          ...prev,
          tours: {
            ...prev.tours,
            [tourId]: { skipped: true, version: currentVersion },
          },
        }));
        setRun(false);
        logOnboardingEvent('tour_skipped', { tourId, stepIndex: index });
        return;
      }

      // Handle close button click — persist as skipped so tour doesn't repeat
      if (action === ACTIONS.CLOSE) {
        await updateOnboardingState(tourId, 'skip', currentVersion);
        setState((prev) => ({
          ...prev,
          tours: {
            ...prev.tours,
            [tourId]: { skipped: true, version: currentVersion },
          },
        }));
        setRun(false);
        logOnboardingEvent('tour_skipped', { tourId, stepIndex: index, closedViaButton: true });
        return;
      }

      // In controlled mode, only update stepIndex after a step completes
      // This is critical: STEP_AFTER fires when the step is done and ready to advance
      if (type === EVENTS.STEP_AFTER) {
        if (action === ACTIONS.NEXT) {
          logOnboardingEvent('step_viewed', { tourId, stepIndex: index });
          setStepIndex(index + 1);
        } else if (action === ACTIONS.PREV) {
          setStepIndex(index - 1);
        }
      }
    },
    [tourId, currentVersion],
  );

  // Manual tour start — resets backend state so the tour can be re-triggered
  const startTour = useCallback(async () => {
    // Stop any running tour first so Joyride resets cleanly
    setRun(false);

    // Reset completed/skipped state in backend
    await updateOnboardingState(tourId, 'reset');
    clearLocalState();
    clearOnboardingVisitedSession();
    // Clear local state so auto-start logic won't block
    setState((prev) => {
      const tours = { ...prev.tours };
      delete tours[tourId];
      return { ...prev, tours };
    });

    // Delay to let Joyride fully reset and the page DOM settle
    setTimeout(() => {
      const validSteps = filterValidSteps(allSteps);
      if (validSteps.length > 0) {
        setSteps(validSteps);
        setStepIndex(0);
        setRun(true);
        logOnboardingEvent('tour_started', { tourId, manual: true });
      }
    }, 300);
  }, [allSteps, tourId]);

  // Reset all tours
  const resetAllTours = useCallback(async () => {
    clearLocalState();
    clearOnboardingVisitedSession(); // Clear session tracking so tours show on next page visit
    for (const id of Object.keys(TOUR_VERSIONS) as TourId[]) {
      await updateOnboardingState(id, 'reset');
    }
    setState({ tours: {} });
  }, []);

  // Check if tour is completed
  const isCompleted = state.tours?.[tourId]?.completed;

  // Welcome wizard state
  const isFirstTimeUser = !isLoading && !state.welcomeCompleted && !!user && !isBctStaff(user.role);

  const completeWelcome = useCallback(async () => {
    try {
      await api.patch('/users/me/onboarding', {
        tourId: '__welcome',
        action: 'complete',
        version: 1,
      });
    } catch {
      // Silently fail
    }
    setState((prev) => ({ ...prev, welcomeCompleted: true }));
    logOnboardingEvent('welcome_completed', {});
  }, []);

  // Onboarding checklist state
  const completeChecklistItem = useCallback(async (itemId: string) => {
    try {
      await api.patch('/users/me/onboarding', {
        tourId: `checklist:${itemId}`,
        action: 'complete',
        version: 1,
      });
    } catch {
      // Silently fail
    }
    setState((prev) => ({
      ...prev,
      checklist: { ...prev.checklist, [itemId]: true },
    }));
  }, []);

  const dismissChecklist = useCallback(async () => {
    try {
      await api.patch('/users/me/onboarding', {
        tourId: 'checklist:dismissed',
        action: 'complete',
        version: 1,
      });
    } catch {
      // Silently fail
    }
    setState((prev) => ({
      ...prev,
      checklist: { ...prev.checklist, dismissed: true },
    }));
  }, []);

  return {
    steps,
    run,
    stepIndex,
    handleCallback,
    startTour,
    resetAllTours,
    isCompleted,
    isLoading,
    isFirstTimeUser,
    completeWelcome,
    onboardingState: state,
    completeChecklistItem,
    dismissChecklist,
  };
}
